// src/analysis/herbicidas/herbicidasProcess.jsx
//
// Ejecución del análisis de Herbicidas. Calcado del flujo de Aplicaciones
// Aéreas (datos manuales + archivo de aplicación), con sus propios endpoints
// del GIS, que el servidor ejecuta en segundo plano (POST /analisis/jobs):
//   - Normal : mapping/herbicidas
//   - VRA    : mapping/herbicidas/tasa_variable
// En Normal la aplicación son franjas KML/KMZ/ZIP (polígonos). En VRA también
// se aceptan puntos SHP/ZIP: el servicio detecta cuál llegó. El objetivo VRA
// es la maleza (SHP/ZIP o KML). Las dimensionales las fija el servicio (km/h, msnm).
import pako from 'pako';
import { toast } from 'react-toastify';
import { CANAL_JOB, lanzarAnalisisEnSegundoPlano, tituloAnalisis } from "../../utils/analisisJobs";
import {
    HERBICIDAS_MANUAL_ID_KEY,
    readHerbicidasManualRows,
} from "./herbicidasFetchData";

const CSV_HEADERS = [
    'RESPONSABLE',
    'NOMBRE DE OPERADOR',
    'FECHA INICIO',
    'FECHA FINAL',
    'HORA INICIO',
    'HORA FIN',
    'CODIGO DE MAQUINA',
    'EFICIENCIA',
    'DOSIS TEORICA',
    'VOLUMEN DE AGUA',
    'DOSIS DE PRODUCTO',
    'FORMULACION',
    'TIPO_PRODUCTO',
    'PRODUCTO',
    'HUMEDAD DEL CULTIVO',
    'TCH ESTIMADO'
];

const KEY_MAP = {
    'RESPONSABLE': 'responsable',
    'NOMBRE DE OPERADOR': 'nombreOperador',
    'FECHA INICIO': 'fechaInicio',
    'FECHA FINAL': 'fechaFinal',
    'HORA INICIO': 'horaInicio',
    'HORA FIN': 'horaFin',
    'CODIGO DE MAQUINA': 'codigoMaquina',
    'EFICIENCIA': 'eficiencia',
    'DOSIS TEORICA': 'dosisTeorica',
    'VOLUMEN DE AGUA': 'volumenAguaL',
    'DOSIS DE PRODUCTO': 'dosisProductoLHa',
    'FORMULACION': 'formulacion',
    'TIPO_PRODUCTO': 'tipoProducto',
    'PRODUCTO': 'producto',
    'HUMEDAD DEL CULTIVO': 'humedadCultivo',
    'TCH ESTIMADO': 'tchEstimado'
};

const toDDMMYYYY = (v) => {
    const s = (v ?? '').toString().trim();
    const m = s.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
    if (!m) return s;
    const [, yyyy, mm, dd] = m;
    return `${dd}/${mm}/${yyyy}`;
};

const toHHMMSS = (v) => {
    const s = (v ?? '').toString().trim();
    if (!s) return '';
    if (/^\d{2}:\d{2}:\d{2}$/.test(s)) return s;
    if (/^\d{2}:\d{2}$/.test(s)) return `${s}:00`;
    return s;
};

const esc = (v) => {
    const s = (v ?? '').toString();
    if (s.includes('"') || s.includes(',') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
};

export const herbicidasPlatformLabel = (value) => {
    const v = String(value || '').trim().toUpperCase();
    if (v === 'AUTOPROPULSADO' || v === 'SELF_PROPELLED') return 'Autopropulsado';
    if (v === 'HELICOPTERO' || v === 'HELICOPTER') return 'Helicóptero';
    if (v === 'AVIONETA' || v === 'AIRPLANE') return 'Avioneta';
    return 'Dron';
};

const buildCsvFromManualRows = (rows, materialType = 'LIQUIDO') => {
    const header = CSV_HEADERS.join(',');
    const lines = rows.map(r => CSV_HEADERS.map(h => {
        const key = KEY_MAP[h];
        let value = r?.[key];

        if ((h === 'VOLUMEN DE AGUA') && (value === undefined || value === null || value === '')) {
            value = r?.volumenAgua ?? r?.volumenAguaL;
        }
        if ((h === 'DOSIS DE PRODUCTO') && (value === undefined || value === null || value === '')) {
            value = r?.dosisProducto ?? r?.dosisProductoLHa;
        }
        if (h === 'FORMULACION' || h === 'TIPO_PRODUCTO') {
            value = materialType || 'LIQUIDO';
        }
        if (h === 'FECHA INICIO' || h === 'FECHA FINAL') {
            value = toDDMMYYYY(value);
        }
        if (h === 'HORA INICIO' || h === 'HORA FIN') {
            value = toHHMMSS(value);
        }
        return esc(value);
    }).join(','));
    return [header, ...lines].join('\n');
};

const resolveIdAnalisis = (idMax) => {
    if (idMax) return String(idMax);
    const fromLS = localStorage.getItem(HERBICIDAS_MANUAL_ID_KEY);
    if (fromLS) return fromLS;
    const generated = String(Date.now());
    localStorage.setItem(HERBICIDAS_MANUAL_ID_KEY, generated);
    return generated;
};

export const ejecutarProcesoHerbicidas = async ({
                                                    selectedZipFile,
                                                    idMax,
                                                    idUsuario,
                                                    setProcessingFinished,
                                                    socketSessionID,
                                                    activarEdicionInteractiva = false,
                                                    setShowProgressBar,
                                                    setProgress,
                                                    setTitleLoader,
                                                    materialType,
                                                    machineryType,
                                                    applicationMode = 'CONVENCIONAL',
                                                    targetPolygonFile = null,
                                                    lotePolygonsGeojson = null,
                                                    uiFiltros = {},
                                                }) => {
    setProgress(0);
    setTitleLoader("Iniciando análisis de herbicidas...");
    setShowProgressBar(true);

    try {
        const manualRows = readHerbicidasManualRows();
        if (manualRows.length === 0) {
            throw new Error('Debes ingresar y guardar los datos manuales de Herbicidas antes de ejecutar el análisis.');
        }
        if (!selectedZipFile) {
            throw new Error('Debes subir el archivo de aplicación (franjas KML/KMZ/ZIP; en VRA también puntos SHP/ZIP o el .log del controlador).');
        }

        const idAnalisisToSend = resolveIdAnalisis(idMax);
        const isVar = String(applicationMode || '').toUpperCase() === 'VAR';

        if (isVar && !targetPolygonFile) {
            throw new Error('Para VRA debes subir los polígonos de maleza (objetivo): SHP/ZIP o KML/KMZ.');
        }

        // 1) Datos manuales → colección `herbicidas`
        const csv = buildCsvFromManualRows(manualRows, materialType);
        const gzBlob = new Blob([pako.gzip(csv)], { type: 'application/gzip' });
        const pasos = [{
            ruta: '/mongo/insert_csv',
            campos: { collection_name: 'herbicidas', session_id: CANAL_JOB, id_analisis: idAnalisisToSend },
            archivos: { file: { blob: gzBlob, nombre: 'herbicidas_manual.csv.gz' } },
        }];

        if (isVar) {
            // 2) VRA: franjas o puntos contra maleza
            const loteFeatures = Array.isArray(lotePolygonsGeojson?.features)
                ? lotePolygonsGeojson.features.filter((feature) => feature?.geometry)
                : [];
            const loteBlob = loteFeatures.length > 0
                ? new Blob([JSON.stringify({ type: 'FeatureCollection', features: loteFeatures })], { type: 'application/geo+json' })
                : null;
            pasos.push({
                ruta: '/mapping/herbicidas/tasa_variable',
                campos: {
                    id_analisis: idAnalisisToSend,
                    session_id: CANAL_JOB,
                    equipo: herbicidasPlatformLabel(machineryType),
                    formulation: materialType || 'LIQUIDO',
                    incluir_puntos: 'true',
                },
                archivos: {
                    aplicacion: selectedZipFile,
                    poligonos_objetivo: targetPolygonFile,
                    poligono_lote: loteBlob ? { blob: loteBlob, nombre: 'lote_plataforma.geojson' } : null,
                },
            });
        } else {
            // 3) Modo Normal: franjas aplicadas + métricas
            pasos.push({
                ruta: '/mapping/herbicidas',
                campos: {
                    filtrar: activarEdicionInteractiva ? 'true' : 'false',
                    session_id: CANAL_JOB,
                    formulation: materialType || 'LIQUIDO',
                    platform: machineryType || 'DRONE',
                    id_analisis: idAnalisisToSend,
                },
                archivos: { polygon_path: selectedZipFile },
            });
        }

        setProgress(5);
        setTitleLoader('Enviando archivos al servidor...');
        const respuesta = await lanzarAnalisisEnSegundoPlano({
            tipoAnalisis: 'HERBICIDAS',
            modo: isVar ? 'VRA' : 'NORMAL',
            equipo: machineryType || 'DRONE',
            titulo: tituloAnalisis('HERBICIDAS', { equipo: machineryType || 'DRONE', modo: isVar ? 'VRA' : 'NORMAL' }),
            idUsuario,
            idAnalisis: idAnalisisToSend,
            sesionOrigen: socketSessionID,
            pasos,
            ui: uiFiltros,
        });

        setProgress(10);
        setTitleLoader(isVar
            ? 'VRA de herbicidas en procesamiento en el servidor. El mapa se actualizará al finalizar...'
            : 'Herbicidas en procesamiento en el servidor. El mapa se actualizará al finalizar...');
        toast.info(respuesta?.message || 'El análisis de herbicidas se está procesando en segundo plano.', {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 7000
        });
    } catch (error) {
        console.error("Error al iniciar el proceso de Herbicidas:", error);
        const errorMessage = error?.response?.data?.message || error.message || 'Ocurrió un error, consulta con tu administrador.';
        toast.error(errorMessage, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 6000
        });
        setShowProgressBar(false);
    }
};

// Herbicidas requiere siempre el archivo de aplicación: sin archivo no hay análisis.
export const ejecutarProcesoSinArchivoHerbicidas = async ({ setProcessingFinished, setShowProgressBar }) => {
    toast.error('Herbicidas requiere datos manuales guardados y el KML/ZIP de franjas de aplicación.', {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 5000,
    });
    if (typeof setShowProgressBar === 'function') setShowProgressBar(false);
    if (typeof setProcessingFinished === 'function') setProcessingFinished(true);
};
